import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse } from '../../types';
import { parseQueryParams } from '../../utils';
import { baseQueryWithReauth } from './baseQuery';

export interface IMasterCustomerListQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  companyId?: number;
  customerGroupId?: number;
  salesOrganizationId?: number;
  distributionChannelId?: number;
  divisionId?: number;
  countryId?: number;
  stateId?: number;
  cityId?: number;
  status?: number;
  currencyId?: number;
  incotermsId?: number;
  paymentTermsId?: number;
  [key: string]: any;
}

export interface IMasterMaterialListQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  CompanyID?: number;
  companyId?: number;
  PlantID?: number;
  plantId?: number;
  materialGroup1Id?: number;
  materialGroup2Id?: number;
  materialGroup3Id?: number;
  MaterialGroupID?: number;
  IsActive?: number;
  status?: number;
  currencyId?: number;
  UOMID?: number;
  ProductTypeID?: number;
  ProductGroupID?: number;
  [key: string]: any;
}

export interface IMasterVendorListQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  invitationTypeId?: number;
  businessPartnerTypeId?: number;
  businessPartnerCategoryId?: number;
  cityId?: number;
  stateId?: number;
  countryId?: number;
  status?: number;
  [key: string]: any;
}

export interface IMasterCustomerRow {
  id: number;
  name: string;
  description?: string;
  erpCode?: string;
  code?: string;
  vConnectRequestNo?: string;
  status: number;
  email?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  cityId?: number;
  stateId?: number;
  countryId?: number;
  postalCode?: string;
  panNo?: string;
  gstNo?: string;
  currencyId?: number;
  incotermsId?: number;
  paymentTermsId?: number;
  customerGroupId?: number;
  salesOrganizationId?: number;
  distributionChannelId?: number;
  divisionId?: number;
  assignedPersonId?: number;
  region1Id?: number;
  region2Id?: number;
  region3Id?: number;
  region4Id?: number;
  createdById?: number;
  companyId?: number;
  [key: string]: any;
}

export interface IMasterMaterialRow {
  ID: number;
  MaterialCode?: string;
  ShortDescription?: string;
  Description?: string;
  SapCode?: string;
  ERPMaterialCode?: string;
  vConnectRequestNo?: string;
  packSize?: string;
  mrpAmount?: number | string;
  ratePerUnit?: number | string;
  shelfLife?: number;
  shelfLifeInMonths?: number | string;
  noOfCapsulePerUnit?: number;
  IsActive: number;
  CompanyID?: number;
  PlantID?: number;
  MaterialGroupID?: number;
  materialGroup1Id?: number;
  materialGroup2Id?: number;
  materialGroup3Id?: number;
  UOMID?: number;
  currencyId?: number;
  gstId?: number;
  ProductTypeID?: number;
  ProductGroupID?: number;
  [key: string]: any;
}

export interface IMasterVendorRow {
  id: number;
  name: string;
  erpCode?: string;
  vConnectId?: string;
  vConnectRequestNo?: string;
  emailId?: string;
  mobileNo?: string;
  panNo?: string;
  gstNo?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  cityId?: number;
  stateId?: number;
  countryId?: number;
  pinCode?: string;
  invitationTypeId?: number;
  businessPartnerTypeId?: number;
  businessPartnerCategoryId?: number;
  status: number;
  createdAt?: string;
  updatedAt?: string;
  invitationType?: { id: number; name?: string; erpCode?: string; vConnectId?: number };
  businessPartnerType?: { id: number; name?: string; erpCode?: string; vConnectId?: number };
  businessPartnerCategory?: { id: number; name?: string; erpCode?: string; vConnectId?: number };
  country?: { ID: number; Name?: string; Code?: string };
  state?: { ID: number; StateName?: string; StateCode?: string };
  city?: { ID: number; ShortDescription?: string; Code?: string };
  [key: string]: any;
}

export interface MasterPaginatedResponse<T> {
  data: T[];
  count: number;
  page: number;
  pageSize: number;
  totalPages: number;
  message?: string;
}

export const masterListApi = createApi({
  reducerPath: 'masterList',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['CustomerList', 'MaterialList', 'VendorList'],
  endpoints: builder => ({
    getCustomerList: builder.query<
      MasterPaginatedResponse<IMasterCustomerRow>,
      IMasterCustomerListQuery | void
    >({
      query: (params = {}) => ({
        url: `/customer/list${parseQueryParams(params || {})}`,
        method: 'GET',
      }),
      providesTags: ['CustomerList'],
    }),

    getMaterialList: builder.query<
      MasterPaginatedResponse<IMasterMaterialRow>,
      IMasterMaterialListQuery | void
    >({
      query: (params = {}) => ({
        url: `/material/list${parseQueryParams(params || {})}`,
        method: 'GET',
      }),
      providesTags: ['MaterialList'],
    }),

    getVendorList: builder.query<
      MasterPaginatedResponse<IMasterVendorRow>,
      IMasterVendorListQuery | void
    >({
      query: (params = {}) => ({
        url: `/vendor/list${parseQueryParams(params || {})}`,
        method: 'GET',
      }),
      providesTags: ['VendorList'],
    }),

    exportCustomerList: builder.query<Blob, IMasterCustomerListQuery | void>({
      query: (params = {}) => ({
        url: `/customer/export${parseQueryParams(params || {})}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    exportMaterialList: builder.query<Blob, IMasterMaterialListQuery | void>({
      query: (params = {}) => ({
        url: `/material/export${parseQueryParams(params || {})}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    exportVendorList: builder.query<Blob, IMasterVendorListQuery | void>({
      query: (params = {}) => ({
        url: `/vendor/export${parseQueryParams(params || {})}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    downloadCustomerSample: builder.query<Blob, void>({
      query: () => ({
        url: `/customer/bulk-upload/sample`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    downloadMaterialSample: builder.query<Blob, void>({
      query: () => ({
        url: `/master/material/bulk-upload/sample`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    downloadVendorSample: builder.query<Blob, void>({
      query: () => ({
        url: `/vendor/upload/sample`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    uploadCustomerBulk: builder.mutation<any, FormData>({
      query: formData => ({
        url: `/customer/bulk-upload`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['CustomerList'],
    }),

    uploadMaterialBulk: builder.mutation<any, FormData>({
      query: formData => ({
        url: `/master/material/bulk-upload`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['MaterialList'],
    }),

    updateVendor: builder.mutation<
      BackendResponse<any>,
      { id: number; data: Partial<IMasterVendorRow> }
    >({
      query: ({ id, data }) => ({
        url: `/vendor-management/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['VendorList'],
    }),

    uploadVendorBulk: builder.mutation<any, FormData>({
      query: formData => ({
        url: `/vendor/upload`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['VendorList'],
    }),
  }),
});

export const {
  useGetCustomerListQuery,
  useLazyGetCustomerListQuery,
  useGetMaterialListQuery,
  useLazyGetMaterialListQuery,
  useGetVendorListQuery,
  useLazyGetVendorListQuery,
  useLazyExportCustomerListQuery,
  useLazyExportMaterialListQuery,
  useLazyExportVendorListQuery,
  useLazyDownloadCustomerSampleQuery,
  useLazyDownloadMaterialSampleQuery,
  useLazyDownloadVendorSampleQuery,
  useUploadCustomerBulkMutation,
  useUploadMaterialBulkMutation,
  useUploadVendorBulkMutation,
  useUpdateVendorMutation,
} = masterListApi;

export default masterListApi;
